/**
 * SPRINT-035B: Main hosted checkout page (Vite SPA).
 *
 * URL: /sessions/{sessionId}#cs={clientSecret}
 *
 * The client_secret is passed in the URL fragment (#cs=...) on first load
 * and stored in sessionStorage for subsequent navigations.
 *
 * This component is purely a state machine renderer — all logic is in useCheckout().
 */

import { useParams } from 'react-router-dom';
import { useCheckout } from '@/hooks/useCheckout';
import { MethodSelection } from '@/components/MethodSelection';
import { WompiCardTokenization } from '@/components/payments/WompiCardTokenization';
import { StripeExpressCheckout } from '@/components/payments/StripeExpressCheckout';
import { PayzenSmartForm } from '@/components/payments/PayzenSmartForm';
import { WalletConfirm } from '@/components/WalletConfirm';
import { FieldCollection } from '@/components/FieldCollection';
import { Processing } from '@/components/Processing';
import { Polling } from '@/components/Polling';
import { QrDisplay } from '@/components/QrDisplay';
import { ReferenceDisplay } from '@/components/ReferenceDisplay';
import { Success } from '@/components/Success';
import { Failure } from '@/components/Failure';
import { Expired } from '@/components/Expired';
import { LoadError } from '@/components/LoadError';

export default function CheckoutPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const { state, actions } = useCheckout(sessionId!);

  switch (state.status) {
    case 'INIT':
    case 'LOADING_SESSION':
      return <Loading />;

    case 'LOAD_ERROR':
      return <LoadError error={state.error} />;

    case 'METHOD_SELECTION':
      return (
        <MethodSelection
          session={state.session}
          paymentOptions={state.paymentOptions}
          savedMethodOneTap={state.savedMethodOneTap}
          onSelect={actions.selectMethod}
          onSelectSaved={actions.selectSavedMethod}
          onCancel={actions.cancel}
        />
      );

    case 'TOKENIZE_CARD':
      return (
        <WompiCardTokenization
          publicKey={state.publicKey}
          environment={state.environment}
          persistentError={state.tokenizationError}
          onSuccess={actions.cardTokenizationSuccess}
          onError={actions.cardTokenizationError}
          onCancel={actions.cardTokenizationCancel}
        />
      );

    case 'STRIPE_CONFIRM':
      return (
        <StripeExpressCheckout
          session={state.session}
          clientSecret={state.clientSecret}
          publishableKey={state.publishableKey}
          returnUrl={`${window.location.origin}/sessions/${sessionId}/return`}
          onSuccess={actions.stripeConfirmed}
        />
      );

    case 'PAYZEN_FORM':
      return (
        <PayzenSmartForm
          session={state.session}
          formToken={state.formToken}
          formPublicKey={state.formPublicKey}
          onCompleted={actions.payzenFormCompleted}
          onFailed={actions.payzenFormFailed}
        />
      );

    case 'WALLET_CONFIRM':
      return (
        <WalletConfirm
          session={state.session}
          paymentOptions={state.paymentOptions}
          onConfirm={actions.confirmWallet}
          onCancel={actions.cancelWallet}
        />
      );

    case 'COLLECTING_FIELDS': {
      const option = state.paymentOptions.find((o) => o.methodKey === state.selectedMethod);
      return (
        <FieldCollection
          session={state.session}
          selectedMethod={state.selectedMethod}
          displayName={option?.label ?? state.selectedMethod}
          requiredFields={state.requiredFields}
          onSubmit={(values) => actions.submitFields(state.selectedMethod, values)}
          onBack={actions.backToMethods}
        />
      );
    }

    case 'PROCESSING':
      return <Processing />;

    case 'REDIRECTING':
      return <Redirecting />;

    case 'POLLING':
      return <Polling pollStartedAt={state.pollStartedAt} />;

    case 'QR_DISPLAY':
      return <QrDisplay session={state.session} attempt={state.attempt} />;

    case 'REFERENCE_DISPLAY':
      return <ReferenceDisplay session={state.session} attempt={state.attempt} />;

    case 'AWAITING_CONFIRMATION':
      return <AwaitingConfirmation onCheckAgain={actions.checkAgain} />;

    case 'STILL_PROCESSING':
      return <StillProcessing onCheckAgain={actions.checkAgain} />;

    case 'SUCCESS_VERIFYING':
      return <Verifying />;

    case 'SUCCESS':
      return <Success redirecting={!!state.session.success_url} />;

    case 'FAILURE':
      return (
        <Failure
          error={state.error}
          canRetry={state.canRetry}
          onRetry={actions.retry}
          cancelUrl={'cancel_url' in state.session ? state.session.cancel_url : '/'}
        />
      );

    case 'EXPIRED':
      return (
        <Expired
          cancelUrl={state.session?.cancel_url ?? undefined}
        />
      );

    case 'CANCELLED':
      return <Cancelled cancelUrl={state.session.cancel_url} />;
  }
}

// ---------------------------------------------------------------------------
// Inline micro-components (too small to warrant separate files)
// ---------------------------------------------------------------------------

function Loading() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Redirecting() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Redirigiendo al proveedor de pago...</p>
      </div>
    </div>
  );
}

function Verifying() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Verificando pago...</p>
      </div>
    </div>
  );
}

/**
 * AWAITING_CONFIRMATION — payment intent submitted; system awaiting external confirmation.
 * Distinct from STILL_PROCESSING (poll timeout) and POLLING (active polling with attempt display).
 * ADR-098: awaiting_confirmation ≠ awaiting_customer_action — no further customer action needed.
 */
function AwaitingConfirmation({ onCheckAgain }: { onCheckAgain: () => void }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Confirmando pago</h2>
        <p className="text-gray-500 mb-6">
          Tu pago fue enviado y está siendo confirmado. Este proceso puede tomar unos momentos.
        </p>
        <button
          onClick={onCheckAgain}
          className="block w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-brand-700 transition-colors text-center"
        >
          Verificar estado
        </button>
      </div>
    </div>
  );
}

function StillProcessing({ onCheckAgain }: { onCheckAgain: () => void }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Pago en proceso</h2>
        <p className="text-gray-500 mb-6">
          Tu pago está siendo procesado. Puedes cerrar esta ventana con seguridad — te notificaremos cuando se complete.
        </p>
        <button
          onClick={onCheckAgain}
          className="block w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-brand-700 transition-colors text-center"
        >
          Verificar estado
        </button>
      </div>
    </div>
  );
}

function Cancelled({ cancelUrl }: { cancelUrl: string }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Pago cancelado</h2>
        <p className="text-gray-500 mb-6">Has cancelado el proceso de pago.</p>
        <a
          href={cancelUrl}
          className="block w-full bg-gray-800 text-white font-semibold py-3 px-6 rounded-xl hover:bg-gray-900 transition-colors text-center"
        >
          Volver a la tienda
        </a>
      </div>
    </div>
  );
}
